/**
 * Диагностика embed-обмена postMessage. Логирует в консоль браузера:
 *   ⇦ IN   — всё, что присылает ХОСТ айфрейма (parent: Hub / app.aseller.io)
 *   ⇨ OUT  — всё, что шлёт САМ айфрейм родителю (READY, RESIZE, AUTH_SUCCESS…)
 *   ⓘ      — конфиг и решения нашего auth-кода
 *
 * Нужен, чтобы понять почему embed-auth не проходит на конкретном хосте:
 * видно приходит ли AUTH/INIT, с какого origin, в какой структуре, и как на
 * это реагирует наш код (origin-фильтр, обмен токена, mode SDK).
 *
 * Включается флагом __RUNTIME_CONFIG__.EMBED_DEBUG (по умолчанию ON, пока
 * идёт диагностика). Только console-логи, данные наружу не отправляет.
 */

const TAG = '[embed-debug]';

function debugEnabled(): boolean {
  const rc = (window as unknown as { __RUNTIME_CONFIG__?: Record<string, string> }).__RUNTIME_CONFIG__;
  const flag = rc?.EMBED_DEBUG;
  // Явное "false"/"0" выключает; всё остальное (включая отсутствие) — включено.
  return flag !== 'false' && flag !== '0';
}

function safeClone(data: unknown): unknown {
  try {
    return JSON.parse(JSON.stringify(data));
  } catch {
    return data;
  }
}

function typeOf(data: unknown): string {
  if (data && typeof data === 'object' && 'type' in (data as Record<string, unknown>)) {
    return String((data as Record<string, unknown>).type);
  }
  return typeof data;
}

let installed = false;

/**
 * Ставит capturing-listener на входящие message ДО инициализации React/SDK,
 * и (по возможности) оборачивает исходящий window.parent.postMessage.
 * Вызывать один раз из main.tsx как можно раньше.
 */
export function installEmbedDebug(): void {
  if (installed || typeof window === 'undefined') return;
  if (window.parent === window) return; // не в iframe — нечего трейсить
  if (!debugEnabled()) return;
  installed = true;

  // eslint-disable-next-line no-console
  console.info(`${TAG} armed. location=`, window.location.href);

  // ── Входящие (host → iframe). capture=true → раньше слушателей SDK/приложения.
  window.addEventListener(
    'message',
    (e: MessageEvent) => {
      // eslint-disable-next-line no-console
      console.info(
        `${TAG} ⇦ IN   origin=${e.origin || '(null)'} type=${typeOf(e.data)}`,
        safeClone(e.data),
      );
    },
    true,
  );

  // ── Исходящие (iframe → parent). Кросс-доменный parent запрещает
  // переопределять postMessage — тогда OUT-трейс недоступен, ловим в catch.
  try {
    const parent = window.parent;
    const original = parent.postMessage.bind(parent) as typeof parent.postMessage;
    (parent as unknown as { postMessage: unknown }).postMessage = (
      message: unknown,
      ...rest: unknown[]
    ) => {
      // eslint-disable-next-line no-console
      console.info(`${TAG} ⇨ OUT  type=${typeOf(message)} target=${String(rest[0] ?? '')}`, safeClone(message));
      return (original as (...a: unknown[]) => unknown)(message, ...rest);
    };
    // eslint-disable-next-line no-console
    console.info(`${TAG} outbound trace enabled (same-origin parent)`);
  } catch {
    // eslint-disable-next-line no-console
    console.info(
      `${TAG} outbound trace UNAVAILABLE (cross-origin parent) — ` +
        `исходящие из iframe не перехватить, смотри вкладку Network/Frames или логи хоста`,
    );
  }
}

/** Тегированный лог из нашего кода (конфиг, решения auth). No-op если выключено. */
export function embedLog(...args: unknown[]): void {
  if (!debugEnabled()) return;
  // eslint-disable-next-line no-console
  console.info(TAG, 'ⓘ', ...args);
}
